/**
 * Transforms a number into human-readable format with delimiters every three digits
 * @param {number} number     Number to be transformed
 * @param {string} delimiter  Delimiter character, choose between dots or commas
 *
 * @returns {string} Human-readable number representation with delimiters
 */
function delimit(number, delimiter) {
    if (delimiter === void 0) { delimiter = "."; }
    var rgx = /\B(?=(\d{3})+(?!\d))/g;
    var result = "";
    var integerPart = Math.trunc(number);
    var decimalPart = parseFloat((number % 1).toFixed(2));
    var decimalDelimiter = delimiter === "." ? "," : ".";
    result = result + integerPart.toString().replace(rgx, delimiter);
    if (decimalPart)
        result = result + decimalDelimiter + decimalPart.toString().split(".")[1];
    return result;
}
/**
 * Helper function to render a number in a human-readable format
 * @param {number} num Number to be rendered
 * @param {number} decimals Trailing decimal places to be rendered
 * @param {number} step Multiplier between notations
 * @param {string[]} notations List of notations for the rendered string
 * @returns {string} Human-readable number with notation
 */
function humanizeNumber(num, decimals, step, notations) {
    var _a;
    if (decimals === void 0) { decimals = 0; }
    if (step === void 0) { step = 1000; }
    if (notations === void 0) { notations = []; }
    if (num === 0)
        return ("0" +
            (notations.length > 0 && ((_a = notations[0]) === null || _a === void 0 ? void 0 : _a.length) > 0
                ? " ".concat(notations[0])
                : ""));
    var dm = decimals < 0 ? 0 : decimals;
    var i = Math.floor(Math.log(Math.abs(num)) / Math.log(step));
    return (delimit(parseFloat((num / Math.pow(step, i)).toFixed(dm))) +
        " " +
        notations[i]).trim();
}
/**
 * Formats bytes into most-appropriate decimal notation.
 *
 * @param {number} bytes    Number of bytes
 * @param {number} decimals Number of decimal places
 * @returns {string} Human-readable file size string
 *
 * @example
 * ```typescript
 * formatBytes(1248) // "1,2 KB"
 * formatBytes(1248, 2) // "1,25 KB"
 * formatBytes(1248, 2, '.') // 1.25 KB
 * ```
 */
function formatBytes(bytes, decimals) {
    if (decimals === void 0) { decimals = 2; }
    var sizes = ["Byte", "KB", "MB", "GB", "TB", "PB", "EB", "ZB", "YB"];
    return humanizeNumber(Math.abs(bytes), decimals, 1000, sizes);
}
/**
 * Formats bytes into most-appropriate binary notation.
 *
 * @param {number} bytes    Number of bytes
 * @param {number} decimals Number of decimal places
 * @returns {string} Human-readable file size string
 *
 * @example
 * ```typescript
 * formatBytesBinary(1248) // "1,22 KiB"
 * formatBytesBinary(1248, 2, '.') // 1.22 KiB
 * ```
 */
function formatBytesBinary(bytes, decimals) {
    if (decimals === void 0) { decimals = 2; }
    var sizes = [
        "Byte",
        "KiB",
        "MiB",
        "GiB",
        "TiB",
        "PiB",
        "EiB",
        "ZiB",
        "YiB",
    ];
    return humanizeNumber(Math.abs(bytes), decimals, 1024, sizes);
}
/**
 * Formats a number into bahasa Indonesia-appropriate notation
 * @param {number} num Number to be formatted
 * @param {number} decimals Number of decimal places
 * @returns {string} Human-readable number adhering to bahasa Indonesia notations
 */
function formatNumber(num, decimals) {
    if (decimals === void 0) { decimals = 1; }
    var sizes = ["", "rb", "jt", "M", "T"];
    return humanizeNumber(num, decimals, 1000, sizes);
}
/**
 * Formats binary file size into bytes.
 *
 * @param {string} sizeNotation File size with notation
 * @returns {number} File size in bytes
 *
 * @example
 * ```typescript
 * parseFileSizeBinary("10 Byte") // "10"
 * parseFileSizeBinary("10 MB") // "10485760"
 * ```
 */
function parseFileSizeBinary(sizeNotation) {
    var sizes = ["Byte", "KB", "MB", "GB", "TB", "PB", "EB", "ZB", "YB"];
    var notation = sizeNotation.split(" ");
    return parseFloat(notation[0]) * Math.pow(1024, sizes.indexOf(notation[1]));
}

export { delimit, formatBytes, formatBytesBinary, formatNumber, humanizeNumber, parseFileSizeBinary };
